import { PoeCharacterHit } from '@/components/planner/header/character-loader/types'
import React, { ReactNode } from 'react'
import createStore from './factory'

class AppStateData {
  spinner = 0
  importSection: string | undefined = 'save'
  profilesError: string | undefined
  profilesStatus: string | undefined
  profilesCurrentFolder = 0
  profilesShowFolders: 'collapsed' | 'expanding' | 'expanded' = 'expanded'
  saveStatus: string | undefined

  apiCharacters: PoeCharacterHit[] = []
}

type AppStateAction<Key extends keyof AppStateData> = {
  type: Key
  value: AppStateData[Key]
}

type AppStateValue<Key extends keyof AppStateData> =
  | AppStateData[Key]
  | ((prev: AppStateData[Key]) => AppStateData[Key])

const AppState = createStore(
  <Key extends keyof AppStateData>(state: AppStateData, action: AppStateAction<Key>): AppStateData => {
    return { ...state, [action.type]: action.value }
  },
  new AppStateData()
)

const identity = <State,>(state: State) => state

/* eslint-disable react-hooks/rules-of-hooks */

export type EditingTask =
  | {
      type: 'folder'
      id: number
    }
  | {
      type: 'load'
      id: string
    }

export type EditingHandler = (task: EditingTask) => boolean | void

export default class EditingContext {
  static context = React.createContext<EditingContext>(undefined!)
  handlers = new Set<EditingHandler>()
  task: EditingTask | undefined
  state = new AppState()

  addHandler(func: EditingHandler) {
    this.handlers.add(func)
    if (this.task && func(this.task)) {
      this.task = undefined
    }
  }
  removeHandler(func: EditingHandler) {
    this.handlers.delete(func)
  }

  dispatch = (task: EditingTask) => {
    for (const func of this.handlers) {
      if (func(task)) {
        return
      }
    }
    this.task = task
  }

  static Provider({ context, children }: { context: EditingContext; children: ReactNode }) {
    return (
      <EditingContext.context.Provider value={context}>
        <AppState.Provider store={context.state}>{children}</AppState.Provider>
      </EditingContext.context.Provider>
    )
  }

  static useHandler(func: EditingHandler) {
    const instance = React.useContext(this.context)
    React.useLayoutEffect(() => {
      instance.addHandler(func)
      return () => instance.removeHandler(func)
    }, [instance, func])
  }

  static useDispatch() {
    const instance = React.useContext(this.context)
    return instance.dispatch
  }

  static useState<Key extends keyof AppStateData>(key: Key): [AppStateData[Key], (value: AppStateValue<Key>) => void] {
    const dispatch = AppState.useDispatch()
    const store = AppState.useStore()
    const state = AppState.useSelector((state) => state[key])
    const setState = React.useCallback(
      (value: AppStateValue<Key>) => {
        if (typeof value === 'function') value = value(store.getState()[key])
        dispatch({ type: key, value })
      },
      [key, store, dispatch]
    )
    return [state, setState]
  }

  static useStateView<Key extends keyof AppStateData>(key: Key): AppStateData[Key] {
    return AppState.useSelector((state) => state[key])
  }

  static useStateSetter<Key extends keyof AppStateData>(key: Key) {
    const dispatch = AppState.useDispatch()
    const store = AppState.useStore()
    const setState = React.useCallback(
      (value: AppStateValue<Key>) => {
        if (typeof value === 'function') value = value(store.getState()[key])
        dispatch({ type: key, value })
      },
      [key, store, dispatch]
    )
    return setState
  }
}
